import { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Plus,
  Search,
  Filter,
  Trash2,
  Eye,
  Power,
  ChevronLeft,
  ChevronRight,
  Upload,
} from 'lucide-react';
import { toast } from 'sonner';
import { questionsApi, categoriesApi } from '../../services/api';

interface Question {
  id: string;
  categoryId: string;
  type: string;
  difficulty: string;
  points: number;
  isActive: boolean;
  createdAt: string;
  category?: { name: string };
  contents?: { type: string; content: string }[];
}

interface Category {
  id: string;
  name: string;
}

export function QuestionsList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [questions, setQuestions] = useState<Question[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [questionToDelete, setQuestionToDelete] = useState<Question | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const searchTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const page = parseInt(searchParams.get('page') || '1');
  const limit = 20;
  const categoryFilter = searchParams.get('category') || '';
  const searchQuery = searchParams.get('q') || '';

  useEffect(() => {
    loadCategories();
  }, []);

  useEffect(() => {
    loadQuestions();
  }, [page, categoryFilter, searchQuery]);

  const loadCategories = async () => {
    try {
      const { data } = await categoriesApi.getAll();
      setCategories(data.items || data || []);
    } catch (error) {
      console.error('Failed to load categories:', error);
    }
  };

  const loadQuestions = async () => {
    setLoading(true);
    try {
      const { data } = await questionsApi.getAll({
        categoryId: categoryFilter || undefined,
        includeInactive: true,
        limit,
        offset: (page - 1) * limit,
        search: searchQuery || undefined,
      });
      setQuestions(data.items || []);
      setTotal(data.total || 0);
    } catch (error) {
      console.error('Failed to load questions:', error);
      setQuestions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (id: string) => {
    try {
      await questionsApi.toggleActive(id);
      loadQuestions();
      toast.success('Statut mis à jour');
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleDelete = async () => {
    if (!questionToDelete) return;
    setIsDeleting(true);
    try {
      await questionsApi.delete(questionToDelete.id);
      loadQuestions();
      toast.success('Question supprimée');
      setQuestionToDelete(null);
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    } finally {
      setIsDeleting(false);
    }
  };

  const setPage = (newPage: number) => {
    searchParams.set('page', newPage.toString());
    setSearchParams(searchParams);
  };

  const setCategory = (cat: string) => {
    if (cat) {
      searchParams.set('category', cat);
    } else {
      searchParams.delete('category');
    }
    searchParams.set('page', '1');
    setSearchParams(searchParams);
  };

  const totalPages = Math.ceil(total / limit);

  const getQuestionPreview = (question: Question): string => {
    if (!question.contents?.length) return 'Pas de contenu';
    const textContent = question.contents.find((c) => c.type === 'text');
    if (textContent) {
      const text = textContent.content.replace(/<[^>]*>/g, '');
      return text.length > 100 ? text.substring(0, 100) + '...' : text;
    }
    return `[${question.contents[0].type}]`;
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'easy':
        return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
      case 'medium':
        return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400';
      case 'hard':
        return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Questions</h1>
          <p className="text-gray-500 dark:text-gray-400">
            {total} questions au total
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/admin/questions/import"
            className="flex items-center gap-2 px-4 py-2 border border-emerald-600 text-emerald-700 dark:text-emerald-400 rounded-lg hover:bg-emerald-50 dark:hover:bg-emerald-900/20"
          >
            <Upload size={18} />
            Importer Excel
          </Link>
          <Link
            to="/admin/questions/new"
            className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
          >
            <Plus size={18} />
            Nouvelle question
          </Link>
        </div>
      </div>
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text"
                placeholder="Rechercher..."
                defaultValue={searchQuery}
                onChange={(e) => {
                  if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
                  const val = e.target.value;
                  searchTimerRef.current = setTimeout(() => {
                    const next = new URLSearchParams(searchParams);
                    if (val) {
                      next.set('q', val);
                    } else {
                      next.delete('q');
                    }
                    next.set('page', '1');
                    setSearchParams(next);
                  }, 300);
                }}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Filter size={18} className="text-gray-400" />
            <select
              value={categoryFilter}
              onChange={(e) => setCategory(e.target.value)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="">Toutes les catégories</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : questions.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500 dark:text-gray-400">Aucune question trouvée</p>
            <Link
              to="/admin/questions/new"
              className="inline-flex items-center gap-2 mt-4 text-emerald-600 hover:text-emerald-700"
            >
              <Plus size={18} />
              Créer la première question
            </Link>
          </div>
        ) : (
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {questions.map((question) => (
              <div
                key={question.id}
                className="p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
              >
                <div className="flex items-start gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span
                        className={`px-2 py-0.5 text-xs font-medium rounded ${getDifficultyColor(
                          question.difficulty
                        )}`}
                      >
                        {question.difficulty}
                      </span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {question.category?.name || 'Sans catégorie'}
                      </span>
                      <span className="text-xs text-gray-400">•</span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {question.points} pts
                      </span>
                      {!question.isActive && (
                        <span className="px-2 py-0.5 text-xs bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300 rounded">
                          Inactif
                        </span>
                      )}
                    </div>
                    <p className="text-gray-900 dark:text-white truncate">
                      {getQuestionPreview(question)}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Link
                      to={`/admin/questions/${question.id}`}
                      className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg"
                      title="Voir / modifier"
                    >
                      <Eye size={18} />
                    </Link>
                    <button
                      onClick={() => handleToggleActive(question.id)}
                      className={`p-2 rounded-lg transition-colors ${
                        question.isActive
                          ? 'text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/20'
                          : 'text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                      }`}
                      title={question.isActive ? 'Désactiver' : 'Activer'}
                    >
                      <Power size={18} />
                    </button>
                    <button
                      onClick={() => setQuestionToDelete(question)}
                      className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg"
                      title="Supprimer"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {totalPages > 1 && (
          <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Page {page} sur {totalPages}
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(page - 1)}
                disabled={page <= 1}
                className="p-2 border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                <ChevronLeft size={18} />
              </button>
              <button
                onClick={() => setPage(page + 1)}
                disabled={page >= totalPages}
                className="p-2 border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                <ChevronRight size={18} />
              </button>
            </div>
          </div>
        )}
      </div>

      {questionToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="w-full max-w-lg rounded-xl bg-white p-6 shadow-2xl dark:bg-gray-800">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Confirmer la suppression
            </h2>
            <p className="mt-3 text-sm text-gray-600 dark:text-gray-300">
              Vous etes sur le point de supprimer cette question. Cette action est irreversible.
            </p>

            <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800 dark:border-red-900/50 dark:bg-red-900/20 dark:text-red-300">
              <p className="font-medium">Question cible :</p>
              <p className="mt-1 break-words">{getQuestionPreview(questionToDelete)}</p>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setQuestionToDelete(null)}
                disabled={isDeleting}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={handleDelete}
                disabled={isDeleting}
                className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isDeleting ? 'Suppression...' : 'Confirmer la suppression'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
